import type {
  GenerateStreamRequest,
  StreamMeta,
  StreamToolCallEvent,
} from '../transport/ConversationTransport'
import type { ContextRecipe } from '../context/types'

/**
 * Where a stream is writing. Surfaces interpret the pair as they like: roleplay
 * uses slot + variant (a reroll writes a new variant of an existing slot), while
 * Creator's flat timeline uses the same id for both.
 */
export interface TurnTarget {
  slotId: number | string
  variantId: number | string
}

export interface TurnStreamContext {
  target: TurnTarget
  streamId: string
}

/**
 * How streamed state reaches a surface's store. The runner calls these at fixed
 * lifecycle points; it never touches a store itself.
 */
export interface TimelineBinding {
  /** The stream is about to open: show the pending entry. */
  begin(ctx: TurnStreamContext): void
  /** The provider connection is established (nothing has generated yet). */
  connected?(ctx: TurnStreamContext): void
  /** Reasoning text so far. Ephemeral — never persisted. */
  reasoning?(ctx: TurnStreamContext, reasoning: string): void
  /** Content so far, plus the delta that produced it. */
  content(ctx: TurnStreamContext, delta: string, content: string): void
  /** Generation finished; runs before persistence. */
  complete(ctx: TurnStreamContext, result: { content: string; meta?: StreamMeta }): void
  /** Drop the entry: an abort with nothing worth keeping, or a turn abandoned. */
  remove(ctx: TurnStreamContext): void
  /**
   * Generation failed and there is nothing to show in its place.
   *
   * Optional: a surface that would rather revert (a reroll still has its
   * previous variant) simply omits it and the entry is removed instead.
   */
  failed?(ctx: TurnStreamContext, error: Error): void
}

/** Surface-owned durable writes. */
export interface TurnPersistence {
  /**
   * Persist the finished assistant content. Not called when a turn aborts before
   * producing any content — there is nothing to keep.
   */
  persistAssistant(
    ctx: TurnStreamContext,
    result: { content: string; aborted: boolean; meta?: StreamMeta },
  ): Promise<void>
}

/** Which phase of a turn failed. Surfaces map these onto their own outcomes. */
export type TurnFailurePhase = 'prepare' | 'stream' | 'assistant-persist'

export class TurnError extends Error {
  phase: TurnFailurePhase

  constructor(phase: TurnFailurePhase, message: string, options?: { cause?: unknown }) {
    super(message, options)
    this.name = 'TurnError'
    this.phase = phase
  }
}

export interface TurnOutcome {
  ok: boolean
  /** The stream was stopped — by the user, or by session cancellation. */
  aborted: boolean
  /** Cancelled before any stream opened (navigation, disposal). */
  cancelled: boolean
  content: string
  /**
   * Set when the provider reported an error mid-stream. Such streams end without
   * failing the request, so the turn resolves with `ok: false` and the surface
   * decides how (or whether) to surface it.
   */
  error?: Error
}

/** What the runner should do after a tool call is surfaced. */
export type ToolRoundDecision = 'continue' | 'stop'

export interface GenerationTurnPlan<TInput = unknown> {
  target: TurnTarget
  streamId: string
  binding: TimelineBinding
  persistence: TurnPersistence
  /**
   * The context specification for THIS invocation. Every invocation names its
   * own recipe, which is what lets a turn run a secondary agent whose prompt
   * shares nothing with the primary one.
   */
  recipe: ContextRecipe<TInput>
  /**
   * Read the recipe's input. Resolved inside the turn, after `prepare`, so
   * context sees whatever preparation produced — a persisted user message
   * today, a secondary agent's findings later.
   */
  buildInput: () => TInput
  /** Everything about the request except its messages. */
  request: Omit<GenerateStreamRequest, 'messages' | 'streamId'>
  /** Consulted at every phase edge; true means abandon without side effects. */
  isCancelled?: () => boolean
  /**
   * Whether an abort that produced no content should drop the pending entry.
   *
   * True by default: an empty aborted invocation has no assistant content to
   * persist, so its pending entry should disappear. A caller may retain the
   * entry only when its own product lifecycle requires an explicit placeholder.
   */
  dropEntryOnEmptyAbort?: boolean
  hooks?: {
    /**
     * Runs after the pending entry appears and before context is composed.
     * Owns whatever must be durable before generation starts (roleplay persists
     * the user message here). Throwing fails the turn in the `prepare` phase;
     * the runner removes the pending entry, and the hook is responsible for
     * rolling back anything it created itself.
     */
    prepare?: () => Promise<void>
    /** A tool call arrived mid-stream. */
    onToolCall?: (event: StreamToolCallEvent) => Promise<ToolRoundDecision | void> | ToolRoundDecision | void
    /** The stream produced a tool call but no content yet. */
    onToolCallStart?: (info: { toolName: string | null }) => void
    /** Runs after a turn settles, successfully or not. */
    finalize?: (outcome: TurnOutcome) => Promise<void> | void
  }
  /** Receives the live stream handle so callers can abort mid-flight. */
  onStreamHandle?: (handle: { abort: () => void }) => void
}
